import React, { useEffect, useState } from "react";
import Select, { components } from "react-select";
import "./StatusSelect.scss";

/** Normalize status category name → tone class used by the pill styles. */
export const getStatusToneClass = (option) => {
  if (!option) return "default";

  const raw = String(option.className || option.statusCategoryName || "")
    .toLowerCase()
    .replace(/\s+/g, "");

  if (raw === "closed") return "close";
  if (["notstarted", "active", "testing", "done", "close"].includes(raw)) {
    return raw;
  }

  if (option.isComplete) return "done";

  const label = String(option.label || "").toLowerCase();
  if (/test|review|pre.?prod|production/.test(label)) return "testing";
  if (/done|complete|closed/.test(label)) return "done";
  if (/progress|re.?open|open|active/.test(label)) return "active";
  if (/to.?do|backlog|not.?started/.test(label)) return "notstarted";

  return "default";
};

const StatusPill = ({ option }) => {
  if (!option) return null;
  const tone = getStatusToneClass(option);

  return (
    <span className={`status-pill ${tone}`}>
      <span className="status-pill-label">{option.label}</span>
    </span>
  );
};

/** Closed-control value: colored dot + text (fits a normal dropdown field). */
const StatusValue = ({ option }) => {
  if (!option) return null;
  const tone = getStatusToneClass(option);

  return (
    <span className={`status-value ${tone}`}>
      <span className="status-value-dot" aria-hidden="true" />
      <span className="status-value-label">{option.label}</span>
    </span>
  );
};

const CustomOption = (props) => {
  const { data, isFocused, innerProps, innerRef } = props;

  return (
    <div
      ref={innerRef}
      {...innerProps}
      className={`status-select-option${isFocused ? " is-focused" : ""}`}
    >
      <StatusPill option={data} />
    </div>
  );
};

const CustomSingleValue = (props) => (
  <components.SingleValue {...props}>
    <StatusValue option={props.data} />
  </components.SingleValue>
);

const StatusSelect = ({ options, initstate, onSelectionChange, id }) => {
  const [selectedOption, setSelectedOption] = useState(null);

  useEffect(() => {
    setSelectedOption(initstate);
  }, [initstate]);

  const handleChange = (option) => {
    setSelectedOption(option);
    onSelectionChange(option, id);
  };

  return (
    <div className="status-select-wrap">
      <Select
        value={selectedOption}
        options={options}
        placeholder="Select Status"
        classNamePrefix="react-status-select"
        isSearchable={false}
        components={{
          Option: CustomOption,
          SingleValue: CustomSingleValue,
          IndicatorSeparator: () => null,
        }}
        menuPortalTarget={document.body}
        menuPosition="absolute"
        menuPlacement="auto"
        styles={{
          control: (provided, state) => ({
            ...provided,
            minHeight: 35,
            height: 35,
            borderRadius: 5,
            borderColor: state.isFocused
              ? "var(--inputBorderHover, #c0c0c0)"
              : "var(--inputBorder, #e0e0e0)",
            boxShadow: "none",
            backgroundColor: "#fff",
            cursor: "pointer",
            "&:hover": {
              borderColor: "var(--inputBorderHover, #c0c0c0)",
            },
          }),
          valueContainer: (provided) => ({
            ...provided,
            padding: "0 8px",
            height: 33,
          }),
          singleValue: (provided) => ({
            ...provided,
            margin: 0,
            maxWidth: "calc(100% - 8px)",
          }),
          indicatorsContainer: (provided) => ({
            ...provided,
            height: 33,
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            padding: "0 8px",
            color: "#9ca3af",
          }),
          menu: (provided) => ({
            ...provided,
            minWidth: 200,
            borderRadius: 8,
            overflow: "hidden",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
            border: "1px solid #e2e8f0",
            zIndex: 1000,
          }),
          menuList: (provided) => ({
            ...provided,
            padding: "6px 0",
            maxHeight: 280,
          }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          placeholder: (provided) => ({
            ...provided,
            fontSize: 12,
            color: "#94a3b8",
          }),
        }}
        onChange={handleChange}
      />
    </div>
  );
};

export default StatusSelect;
